import { useEffect, useSyncExternalStore } from 'react';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { bffQuery } from '../lib/bffClient';

/**
 * Can this person buy پارسه — is a «شارژ» / «افزایش اعتبار» link worth showing?
 * (PM-0211, docs/platform-money/CONTRACT.md §5, §6.)
 *
 * Every top-up link in this panel goes to the account app's credits page, where
 * a person tops up their OWN wallet through the Iranian gateways, in toman. So a
 * link is shown only when the deployment sells credit — the clinic BFF's
 * `creditPrices.purchaseAvailable` — AND its price is a toman one (`IRT`, or no
 * currency): a dollar price has no rail there yet, and no rail means no checkout
 * (CONTRACT rule 5). The out-of-credit messages themselves always show.
 *
 * Until the BFF has answered — and from a BFF that predates the field, which
 * refuses the query — the brand's market decides: Iran yes, as always; any other
 * market no. So neither deployment flashes a link it then takes away.
 *
 * Read once per page load and shared (a module-level store, like the kit's
 * price list); a failed read is not remembered, so the next mount asks again.
 */

export const CREDIT_PURCHASE_QUERY = `
  query CreditPurchase {
    creditPrices {
      purchaseAvailable
      creditPrice { amountMinor currency }
    }
  }
`;

let answer = null;          // { purchaseAvailable, currency } once the BFF has said
let inflight = null;
const listeners = new Set();
const emit = () => { for (const fn of listeners) fn(); };

/** A `creditPrices` answer → what this panel keeps, or null when it says nothing. */
export function purchaseFromAnswer(list) {
  if (!list || typeof list.purchaseAvailable !== 'boolean') return null;
  const currency = typeof list.creditPrice?.currency === 'string' ? list.creditPrice.currency.trim() : '';
  return { purchaseAvailable: list.purchaseAvailable, currency };
}

/** Whether a top-up link is offered, right now. */
export function canTopUp() {
  if (!answer) return isIranMarket();
  return answer.purchaseAvailable && (!answer.currency || answer.currency === 'IRT');
}

/** Ask the BFF once. Never rejects: every outcome has an answer (the market). */
export function loadCreditPurchase() {
  if (answer) return Promise.resolve(answer);
  if (inflight) return inflight;
  inflight = Promise.resolve()
    .then(() => bffQuery(CREDIT_PURCHASE_QUERY))
    .then((data) => {
      const said = purchaseFromAnswer(data?.creditPrices);
      if (said) { answer = said; emit(); }
    })
    .catch(() => { /* offline, or a BFF without the field: the market decides */ })
    .finally(() => { inflight = null; })
    .then(() => answer);
  return inflight;
}

/** Back to "not asked yet". Tests only. */
export function resetCreditPurchase() {
  answer = null;
  inflight = null;
  emit();
}

const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const snapshot = () => answer;

/**
 * `canTopUp()` as a hook. `load` false follows the store without asking: a
 * surface whose link exists only once someone is short of credit asks then.
 */
export function useCreditPurchase(load = true) {
  useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => { if (load) loadCreditPurchase(); }, [load]);
  return canTopUp();
}

export default useCreditPurchase;
